import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FunctionComponent, SVGProps } from 'react';
import Pagination from '../../components/Pagination';
import {
  NewEngagementIcon,
  SearchIcon,
  EditSquareIcon,
  TrashIcon,
  CheckmarkIcon,
  StatBoltIcon,
  StatCalendarIcon,
  StatCheckIcon,
  StatWarningIcon,
} from '../../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;
type DirStatus = 'inprogress' | 'pending' | 'assigned' | 'completed';
type StatKey = 'all' | 'active' | 'scheduled' | 'completed' | 'overdue';

interface Direction {
  id: string;
  title: string;
  description: string;
  assignee: string;
  avatarBg: string;
  avatarColor: string;
  schedule: string;
  scheduleToday: boolean;
  status: DirStatus;
  category: Exclude<StatKey, 'all'>;
}

const DIRECTIONS: Direction[] = [
  { id: 'DD-1023', title: 'Visit Booth Presidents', description: 'Constituency outreach and coordination', assignee: 'Ravi', avatarBg: 'bg-[#dbeafe]', avatarColor: 'text-[#0061ff]', schedule: 'Today', scheduleToday: true, status: 'inprogress', category: 'active' },
  { id: 'DD-1024', title: 'Meet Panchayat Members', description: 'Address local infrastructure delays', assignee: 'Anita', avatarBg: 'bg-[#e0e7ff]', avatarColor: 'text-[#4f46e5]', schedule: 'Today', scheduleToday: true, status: 'pending', category: 'scheduled' },
  { id: 'DD-1025', title: 'Verify Water Complaint', description: 'Site inspection for pipe leakage', assignee: 'Mahesh', avatarBg: 'bg-[#f3e8ff]', avatarColor: 'text-[#9333ea]', schedule: 'Tomorrow', scheduleToday: false, status: 'assigned', category: 'active' },
  { id: 'DD-1026', title: 'Collect Voter List', description: 'Updated register from election office', assignee: 'Ravi', avatarBg: 'bg-[#dbeafe]', avatarColor: 'text-[#0061ff]', schedule: 'Jul 12', scheduleToday: false, status: 'completed', category: 'completed' },
];

const STATUS_META: Record<DirStatus, { label: string; pill: string; dot?: string; text: string; check?: boolean }> = {
  inprogress: { label: 'In Progress', pill: 'bg-[#f0fdf4]', dot: 'bg-[#22c55e]', text: 'text-[#15803d]' },
  pending: { label: 'Pending', pill: 'bg-[#f1f5f9]', dot: 'bg-[#94a3b8]', text: 'text-[#475569]' },
  assigned: { label: 'Assigned', pill: 'bg-[#f1f5f9]', dot: 'bg-[#0061ff]', text: 'text-[#475569]' },
  completed: { label: 'Completed', pill: 'bg-[#f0fdf4]', text: 'text-[#15803d]', check: true },
};

interface StatCard {
  key: StatKey;
  value: string;
  label: string;
  Icon?: IconType;
  iconColor?: string;
  total?: boolean;
  overdue?: boolean;
}

/** Card values are the full-dataset figures from the design. */
const STAT_CARDS: StatCard[] = [
  { key: 'all', value: '256', label: 'Total Command Load', total: true },
  { key: 'active', value: '42', label: 'Active', Icon: StatBoltIcon, iconColor: 'text-[#22c55e]' },
  { key: 'scheduled', value: '18', label: 'Scheduled', Icon: StatCalendarIcon, iconColor: 'text-[#0061ff]' },
  { key: 'completed', value: '176', label: 'Completed', Icon: StatCheckIcon, iconColor: 'text-[#16a34a]' },
  { key: 'overdue', value: '12', label: 'Overdue', Icon: StatWarningIcon, iconColor: 'text-[#ef4444]', overdue: true },
];

const CARD_COUNT: Record<StatKey, string> = {
  all: '256',
  active: '42',
  scheduled: '18',
  completed: '176',
  overdue: '12',
};

const GRID = 'grid grid-cols-[96px_minmax(0,1fr)_190px_128px_160px_120px] items-center';
const TH = 'text-xs font-bold uppercase tracking-[0.6px] text-[#64748b]';

function StatusPill({ status }: { status: DirStatus }) {
  const m = STATUS_META[status];
  return (
    <span className={['inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', m.pill, m.text].join(' ')}>
      {m.check ? (
        <CheckmarkIcon className="size-2.5" />
      ) : (
        <span className={['size-1.5 rounded-full', m.dot].join(' ')} />
      )}
      {m.label}
    </span>
  );
}

export default function DailyDirectionsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StatKey>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DIRECTIONS.filter((d) => {
      if (filter !== 'all' && d.category !== filter) return false;
      if (
        q &&
        !(
          d.id.toLowerCase().includes(q) ||
          d.title.toLowerCase().includes(q) ||
          d.assignee.toLowerCase().includes(q)
        )
      )
        return false;
      return true;
    });
  }, [query, filter]);

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-[0.6px] text-brand">
            Strategic Operations
          </span>
          <h1 className="text-2xl font-semibold text-ink">Daily Directions</h1>
        </div>
        <button
          type="button"
          onClick={() => navigate('/user/directions/new')}
          className="flex items-center justify-center gap-2 self-start rounded-lg bg-brand px-5 py-2.5 text-base font-medium text-white shadow-btn transition-colors hover:bg-brand-dark sm:self-auto"
        >
          <NewEngagementIcon className="size-4" />
          New Direction
        </button>
      </div>

      {/* Stat cards — clickable filters; the selected card takes the blue treatment */}
      <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 xl:grid-cols-5">
        {STAT_CARDS.map((c) => {
          const selected = filter === c.key;
          const container = selected
            ? 'bg-brand text-white'
            : c.overdue
              ? 'border border-[#fee2e2] bg-[#fef2f2]'
              : 'border border-[#f1f5f9] bg-white hover:border-brand/40';

          if (c.total) {
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilter(c.key)}
                aria-pressed={selected}
                className={['relative flex h-44 flex-col justify-between overflow-hidden rounded-xl p-6 text-left shadow-card transition', container].join(' ')}
              >
                {selected && <div className="absolute -bottom-6 -right-6 size-36 rounded-full bg-white/10" />}
                <span
                  className={['relative text-sm font-medium uppercase leading-5 tracking-[0.7px]', selected ? 'text-[#dbeafe]' : 'text-ink-secondary'].join(' ')}
                >
                  Total Command Load
                </span>
                <div className="relative flex items-baseline gap-2">
                  <span className={['text-5xl font-bold leading-none', selected ? 'text-white' : 'text-ink'].join(' ')}>
                    {c.value}
                  </span>
                  <span className={['text-base font-medium', selected ? 'text-[#dbeafe]' : 'text-ink-secondary'].join(' ')}>
                    Tasks
                  </span>
                </div>
              </button>
            );
          }

          const numColor = selected ? 'text-white' : c.overdue ? 'text-[#dc2626]' : 'text-ink';
          const labelColor = selected ? 'text-white/80' : c.overdue ? 'text-[#f87171]' : 'text-ink-secondary';
          const iconColor = selected ? 'text-white' : c.iconColor;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setFilter(c.key)}
              aria-pressed={selected}
              className={['flex h-44 flex-col justify-center gap-3 rounded-xl p-6 text-left shadow-card transition', container].join(' ')}
            >
              <span className="flex size-10 items-center justify-center">
                {c.Icon && <c.Icon className={['size-6', iconColor].join(' ')} />}
              </span>
              <span className={['text-4xl font-bold leading-10', numColor].join(' ')}>{c.value}</span>
              <span className={['text-xs font-semibold uppercase tracking-[0.6px]', labelColor].join(' ')}>
                {c.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Global search */}
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#99a2b2]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Global search for direction IDs, personnel, or locations..."
          className="w-full rounded-lg border border-transparent bg-white pl-11 pr-4 text-base text-ink placeholder:text-[#99a2b2] shadow-card focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#f1f5f9] bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[940px]">
            {/* Header */}
            <div className={[GRID, 'border-b border-[#f1f5f9] bg-[#f8fafc] px-6 py-4'].join(' ')}>
              <span className={TH}>ID</span>
              <span className={TH}>Direction</span>
              <span className={TH}>Assignee</span>
              <span className={TH}>Schedule</span>
              <span className={TH}>Status</span>
              <span className={[TH, 'text-right'].join(' ')}>Actions</span>
            </div>

            {/* Rows */}
            {rows.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-ink-secondary">No directions found.</div>
            ) : (
              rows.map((d, i) => (
                <div key={d.id} className={[GRID, 'px-6 py-5', i > 0 ? 'border-t border-[#f1f5f9]' : ''].join(' ')}>
                  {/* ID */}
                  <span className="text-sm font-semibold text-brand">{d.id}</span>

                  {/* Direction */}
                  <div className="flex min-w-0 flex-col gap-0.5 pr-4">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/user/directions/${d.id}`, {
                          state: { title: d.title, dirId: d.id },
                        })
                      }
                      className="text-left text-base font-semibold text-brand transition-colors hover:text-brand-dark"
                    >
                      {d.title}
                    </button>
                    <span className="text-sm text-[#64748b]">{d.description}</span>
                  </div>

                  {/* Assignee */}
                  <div className="flex items-center gap-2">
                    <span className={['flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold', d.avatarBg, d.avatarColor].join(' ')}>
                      {d.assignee.charAt(0)}
                    </span>
                    <span className="text-sm font-medium text-[#334155]">{d.assignee}</span>
                  </div>

                  {/* Schedule */}
                  <div>
                    <span
                      className={[
                        'inline-flex items-center rounded px-2.5 py-1 text-xs font-medium',
                        d.scheduleToday ? 'bg-[#eff6ff] text-brand' : 'bg-[#f1f5f9] text-[#475569]',
                      ].join(' ')}
                    >
                      {d.schedule}
                    </span>
                  </div>

                  {/* Status */}
                  <div>
                    <StatusPill status={d.status} />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 text-ink-secondary">
                    <button type="button" aria-label="Edit" className="transition-colors hover:text-brand">
                      <EditSquareIcon className="size-4" />
                    </button>
                    <button type="button" aria-label="Delete" className="transition-colors hover:text-red-500">
                      <TrashIcon className="size-4" />
                    </button>
                  </div>
                </div>
              ))
            )}

            {/* Footer */}
            {(() => {
              const total = Number(CARD_COUNT[filter]);
              return (
                <Pagination
                  rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
                  rangeEnd={Math.min(page * pageSize, total)}
                  total={total}
                  page={page}
                  totalPages={Math.max(1, Math.ceil(total / pageSize))}
                  onChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={(v) => {
                    setPageSize(v);
                    setPage(1);
                  }}
                />
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
